import { Box } from 'src/design/atoms/Box';
import { Grid } from 'src/design/atoms/Grid';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import React from 'react';
import { formatRate } from 'src/utils';
import {
  IRateFormulaStep,
  RATE_FORMULA_VALUE_FORMAT,
} from 'src/app/timesheets/types';

export interface IRateCardProps {
  label: string;
  value: string;
  highlight?: boolean;
  subdued?: boolean;
}

export const RateCard: React.FC<IRateCardProps> = ({
  label,
  value,
  highlight = false,
  subdued = false,
}) => (
  <Paper
    withBorder
    p="sm"
    bg={highlight ? 'gray.0' : undefined}
    style={{
      borderColor: subdued
        ? 'var(--mantine-color-gray-2)'
        : highlight
        ? 'var(--mantine-color-gray-4)'
        : 'var(--mantine-color-gray-3)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      opacity: subdued ? 0.7 : 1,
    }}
  >
    <Stack gap="xs" justify="space-between" flex={1}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={subdued ? 500 : 600} c="bright">
        {value}
      </Text>
    </Stack>
  </Paper>
);

export interface IOperatorProps {
  symbol: string;
}

const Operator: React.FC<IOperatorProps> = ({ symbol }) => (
  <Grid.Col
    span={1}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Text size="sm" c="dimmed">
      {symbol}
    </Text>
  </Grid.Col>
);

const formatStepValue = (step: IRateFormulaStep): string => {
  switch (step.format) {
    case RATE_FORMULA_VALUE_FORMAT.Multiplier:
      return `${step.value.toFixed(2)}x`;
    case RATE_FORMULA_VALUE_FORMAT.Currency:
    default:
      return formatRate(step.value);
  }
};

export interface IRateFormulaProps {
  title: string;
  steps: IRateFormulaStep[];
}

/**
 * Renders an ordered list of formula steps with operators between them. Backend
 * controls the step ordering, span, operators, and which step is highlighted —
 * this component only formats values and lays them out in the grid.
 */
export const RateFormula: React.FC<IRateFormulaProps> = ({ title, steps }) => {
  const usedSpan = steps.reduce((acc, step) => {
    const opSpan = step.operatorAfter ? 1 : 0;
    return acc + step.span + opSpan;
  }, 0);
  const fillerSpan = 12 - usedSpan;

  return (
    <Box>
      <Text c="bright" mb="xs">
        {title}
      </Text>
      <Grid gutter="xs" align="stretch">
        {steps.map((step, idx) => (
          <React.Fragment key={idx}>
            <Grid.Col span={step.span}>
              <RateCard
                label={step.label}
                value={formatStepValue(step)}
                highlight={step.highlight}
              />
            </Grid.Col>
            {step.operatorAfter && <Operator symbol={step.operatorAfter} />}
          </React.Fragment>
        ))}
        {fillerSpan > 0 && <Grid.Col span={fillerSpan} />}
      </Grid>
    </Box>
  );
};
